import { useQueryResponse, useQueryResponseData } from './query-response';
import {
  ID,
  ItemData,
  ItemWithId,
  ListViewContextProps,
  QueryResponseContextProps,
  initialListView
} from 'components/table/types';
import { createContext, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { WithChildren } from 'ts-types';
import {
  calculateIsAllDataSelected,
  calculatedGroupingIsDisabled,
  groupingOnSelect,
  groupingOnSelectAll
} from 'utils/queries';

const ListViewContext = createContext<ListViewContextProps>(initialListView);

type Props<T> = WithChildren & {
  queryResponseContext: React.Context<QueryResponseContextProps<T>>;
};

const ListViewProvider = <T extends ItemWithId>(props: Props<T>) => {
  const [selected, setSelected] = useState<Array<ID>>(initialListView.selected);
  const [itemIdForUpdate, setItemIdForUpdate] = useState<ItemData>();

  const { isLoading } = useQueryResponse(props.queryResponseContext);
  const data = useQueryResponseData(props.queryResponseContext);
  const disabled = useMemo(() => calculatedGroupingIsDisabled(isLoading, data), [isLoading, data]);
  const isAllSelected = useMemo(() => calculateIsAllDataSelected(data, selected), [data, selected]);
  const [expandedRowIds, setExpandedRowIds] = useState<ID[]>();

  return (
    <ListViewContext.Provider
      value={{
        selected,
        itemIdForUpdate,
        setItemIdForUpdate,
        disabled,
        isAllSelected,
        expandedRowIds,
        setExpandedRowIds,
        onSelect: (id: ID) => {
          groupingOnSelect(id, selected, setSelected);
        },
        onSelectAll: () => {
          groupingOnSelectAll(isAllSelected, setSelected, data);
        },
        clearSelected: () => {
          setSelected([]);
        },
        onSelectSingle: (id: ID) => {
          setSelected([id]);
        }
      }}
    >
      {props.children}
    </ListViewContext.Provider>
  );
};

const useListView = () => useContext(ListViewContext);

export { ListViewProvider, useListView };
